'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback } from 'react';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { SITE } from '@/app/constants';
import { useFetchRecreateData } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, ImageUploadFile, TaskInfo } from '@/app/types';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { MAX_REFERENCE_IMAGE_COUNT, MultiImageUpload } from '../_components/MultiImageUpload';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { useRunTempUploadBatch } from '../_hooks/use-run-temp-upload-batch';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolTaskRecreatePatch,
  createPromptListHelpers,
  createToolImagePromptFromUploadFile,
} from '../_utils';
import { getToolTempImagesPendingItems } from '../_utils/tool-temp-upload-items';
import { OneClickVideoProductionSettings } from './_components/OneClickVideoProductionSettings';

type OneClickScenario = 'unspecified' | 'general' | 'ecommerce_ad';

const DEFAULT_VOICE_LANG = SITE === 'china' ? 'zh' : 'en';

const OneClickVideoProductionForm: FC = () => {
  const t = useTranslations();
  const storeApi = useStoreApi();

  const { settings, updateSettings } = useToolForm('one_click');

  const { updateOneClickVideoProductionFormParams, imagePrompts, textPrompt, userInfo } = useStore((state) => ({
    updateOneClickVideoProductionFormParams: state.updateOneClickVideoProductionFormParams,
    imagePrompts: state.oneClickVideoProductionFormParams.imagePrompts,
    textPrompt: state.oneClickVideoProductionFormParams.textPrompt || '',
    userInfo: state.userInfo,
  }));

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().oneClickVideoProductionFormParams.imagePrompts,
          (prompts) =>
            updateOneClickVideoProductionFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateOneClickVideoProductionFormParams, userInfo],
  );

  const handleTaskSuccess = useCallback(
    async (task: TaskInfo, inspirationId?: string, source?: string) => {
      const patch = await buildToolTaskRecreatePatch({
        tool: 'one_click',
        task,
        baseSettings: settings,
        inspirationId,
        inspirationFrom: source,
      });
      applyToolFormParamsPatch(updateOneClickVideoProductionFormParams, patch);
    },
    [settings, updateOneClickVideoProductionFormParams],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const handleRemoveImage = useCallback(
    (fileId: string) => {
      const newImagePrompts = imagePrompts.filter((img) => img.id !== fileId);
      updateOneClickVideoProductionFormParams('imagePrompts', newImagePrompts);
    },
    [imagePrompts, updateOneClickVideoProductionFormParams],
  );

  const handleImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    const imagePrompt = createToolImagePromptFromUploadFile(file);

    const currentImagePrompts = storeApi.getState().oneClickVideoProductionFormParams.imagePrompts;
    const existingIndex = currentImagePrompts.findIndex((img) => img.id === file.id);

    if (existingIndex !== -1) {
      const newImagePrompts = [...currentImagePrompts];
      newImagePrompts[existingIndex] = imagePrompt;
      updateOneClickVideoProductionFormParams('imagePrompts', newImagePrompts);
    } else if (currentImagePrompts.length < MAX_REFERENCE_IMAGE_COUNT) {
      const newImagePrompts = [...currentImagePrompts, imagePrompt];
      updateOneClickVideoProductionFormParams('imagePrompts', newImagePrompts);
    }

    if (file.status === 'error' || file.status === 'removed') {
      const updatedImagePrompts = storeApi.getState().oneClickVideoProductionFormParams.imagePrompts;
      const indexToRemove = updatedImagePrompts.findIndex((img) => img.id === file.id);
      if (indexToRemove !== -1) {
        handleRemoveImage(file.id);
      }
    }
  });

  const getSelectedImage = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(blob);
      });
      return { blob, width: img.width, height: img.height };
    } catch {
      return null;
    }
  }, []);

  const handleScenarioChange = useCallback(
    (value: OneClickScenario) => {
      const nextSettings = { ...settings, biz_scene: value };

      if (value === 'general') {
        const { voice_lang: _voiceLang, ...settingsWithoutVoiceLang } = nextSettings;
        updateOneClickVideoProductionFormParams('settings', settingsWithoutVoiceLang);
        return;
      }

      updateOneClickVideoProductionFormParams('settings', {
        ...nextSettings,
        voice_lang: nextSettings.voice_lang ?? DEFAULT_VOICE_LANG,
      });
    },
    [settings, updateOneClickVideoProductionFormParams],
  );

  const imageUploadFiles: ImageUploadFile[] = imagePrompts.map((img) => ({
    id: img.id,
    status: img.status,
    file: img.prompt || null,
  })) as ImageUploadFile[];

  const hasUploadingMedia = imagePrompts.some((img) => img.status === 'uploading');
  const isSubmitDisabled = hasUploadingMedia || imagePrompts.filter((img) => img.status === 'done').length === 0;
  const remainingImageSlots = Math.max(0, MAX_REFERENCE_IMAGE_COUNT - imagePrompts.length);
  return (
    <ToolFormContainer
      className="bg-[#0a0d10] px-5 py-10"
      title={t('tools.oneClickVideoProduction.title')}
      description={t('tools.oneClickVideoProduction.description')}
      exampleSection={<ExampleMediaPreview tool="one_click" />}
      mediaSection={
        <MediaDropZone
          maxImageCount={remainingImageSlots}
          disabled={remainingImageSlots === 0}
          getSelectedImage={getSelectedImage}
          allowTempMode={true}
          onChange={(file, type) => {
            if (type === 'image') handleImageUpload(file as ImageUploadFile);
          }}
        >
          <MultiImageUpload
            imageList={imageUploadFiles}
            onImageUpload={handleImageUpload}
            onRemoveImage={handleRemoveImage}
            getSelectedImage={getSelectedImage}
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm text-system-white leading-[1.4]">
                {t('tools.oneClickVideoProduction.prompt.label')}
              </div>
            </div>
            <div className="relative flex min-h-[120px] w-full flex-col rounded-16 border border-system-white08 bg-system-white04 p-3 backdrop-blur-[30px]">
              <BasicTextarea
                placeholder={t('tools.oneClickVideoProduction.prompt.placeholder')}
                textPrompt={textPrompt}
                updateTextPrompt={(value) => updateOneClickVideoProductionFormParams('textPrompt', value)}
                showClearButton={!!textPrompt}
                onClearPrompt={() => updateOneClickVideoProductionFormParams('textPrompt', '')}
              />
            </div>
          </div>
        </MediaDropZone>
      }
      settingsSection={
        <OneClickVideoProductionSettings
          scenario={settings?.biz_scene}
          aspectRatio={settings?.aspect_ratio}
          duration={settings?.duration}
          voiceLang={settings?.biz_scene === 'general' ? undefined : settings?.voice_lang}
          onScenarioChange={handleScenarioChange}
          onAspectRatioChange={(value) => updateSettings({ aspect_ratio: value })}
          onDurationChange={(value) => updateSettings({ duration: value })}
          onVoiceLangChange={(value) => updateSettings({ voice_lang: value })}
        />
      }
      submitButton={
        <ToolFormSubmitButton
          taskType="one_click"
          disabled={isSubmitDisabled}
          disabledTip={hasUploadingMedia ? t('create.createForm.error.uploadingMedia') : undefined}
        >
          {t('create.createForm.submit')}
        </ToolFormSubmitButton>
      }
    />
  );
};

export default OneClickVideoProductionForm;
